/**
 * 文件定位：
 * 1. 当前文件位于 Worker 倒角求解 class 层；
 * 2. 负责 SolidWorks Vertex 倒角的 Puppy CAD 内核 构建；
 * 3. 顶点周边三边距离计算通过纯函数完成。
 *
 * 文件职责：
 * 1. 校验单顶点输入与三向距离；
 * 2. 从顶点相邻边构造切角平面；
 * 3. 用半空间布尔切除生成顶点倒角结果。
 */

import {
  extractVertexPoint,
  resolveIncidentEdgeShapesByVertex,
} from '../../common';
import { resolveVertexShapeBySelection } from '../../topology-selection-resolution';
import { buildCutShape, safeDelete } from '../functions/chamferPuppyKernel';
import {
  cross,
  normalizeVector,
  vectorSubtract,
} from '../functions/chamferVector';
import {
  buildVertexChamferCutPoints,
  resolveVertexChamferDistances,
} from '../functions/vertexChamferGeometry';
import type { ChamferShapeBuildInput } from '../types/ChamferShapeBuildInput';
import { ChamferSolver } from './ChamferSolver';

/**
 * 顶点倒角求解器。
 */
export class VertexChamferSolver extends ChamferSolver {
  
  public readonly type = 'vertex' as const;

  /**
   * @inheritdoc
   */
  public build(input: ChamferShapeBuildInput): unknown {
    const { puppy, sourceShape, sourceMetadata, sourceObjectId, definition } = input;
    const vertexSelection = definition.items.find((item) => item.kind === 'vertex');
    if (!vertexSelection) {
      throw new Error('顶点倒角缺少顶点选择');
    }

    const vertexShape = resolveVertexShapeBySelection(
      puppy,
      sourceShape,
      sourceMetadata,
      vertexSelection,
      { sourceObjectId },
    );
    const vertexPoint = extractVertexPoint(puppy, vertexShape);
    if (!vertexPoint) {
      throw new Error('顶点倒角无法解析选中顶点坐标');
    }

    const incidentEdges = resolveIncidentEdgeShapesByVertex(puppy, sourceShape, vertexShape);
    if (incidentEdges.length < 3) {
      throw new Error('顶点倒角至少需要 3 条相交边');
    }
    if (incidentEdges.length !== 3) {
      throw new Error('当前 Vertex Chamfer solver 仅支持三面角顶点主路径');
    }

    const distances = resolveVertexChamferDistances(definition);
    const cutPoints = buildVertexChamferCutPoints(puppy, incidentEdges, vertexPoint, distances);
    const planeNormal = cross(
      vectorSubtract(cutPoints[1]!, cutPoints[0]!),
      vectorSubtract(cutPoints[2]!, cutPoints[0]!),
    );
    const normalizedPlaneNormal = normalizeVector(planeNormal);
    const planePoint = new puppy.gp_Pnt_3(cutPoints[0]![0], cutPoints[0]![1], cutPoints[0]![2]);
    const planeDirection = new puppy.gp_Dir_5(
      normalizedPlaneNormal[0],
      normalizedPlaneNormal[1],
      normalizedPlaneNormal[2],
    );
    const plane = new puppy.gp_Pln_3(planePoint, planeDirection);
    const faceMaker = new puppy.BRepBuilderAPI_MakeFace_3(plane);
    const referencePoint = new puppy.gp_Pnt_3(vertexPoint[0], vertexPoint[1], vertexPoint[2]);

    try {
      const isDone = faceMaker.IsDone as (() => boolean) | undefined;
      if (typeof isDone === 'function' && !isDone.call(faceMaker)) {
        throw new Error('顶点倒角构造切角平面失败');
      }
      const faceGetter = faceMaker.Face as (() => unknown) | undefined;
      const planeFace = typeof faceGetter === 'function' ? faceGetter.call(faceMaker) : null;
      if (!planeFace) {
        throw new Error('顶点倒角未生成切角平面 Face');
      }

      const halfSpaceBuilder = new puppy.BRepPrimAPI_MakeHalfSpace_1(planeFace, referencePoint);
      try {
        const solidGetter = halfSpaceBuilder.Solid as (() => unknown) | undefined;
        const toolShape = typeof solidGetter === 'function' ? solidGetter.call(halfSpaceBuilder) : null;
        if (!toolShape) {
          throw new Error('顶点倒角未生成半空间切除体');
        }
        return buildCutShape(puppy, sourceShape, toolShape);
      } finally {
        safeDelete(halfSpaceBuilder);
      }
    } finally {
      safeDelete(referencePoint);
      safeDelete(faceMaker);
      safeDelete(plane);
      safeDelete(planeDirection);
      safeDelete(planePoint);
    }
  }
}
